import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ordemCompraService, type FiltrosOrdemCompra } from '../services/ordemCompraService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type {
  CriarOrdemCompraRequest,
  AtualizarOrdemCompraRequest,
  AprovarOrdemCompraRequest,
  ProcessarRecebimentoRequest,
  StatusOrdemCompra,
} from '../types';

interface UseOrdemCompraParams {
  pagina?: number;
  tamanhoPagina?: number;
  status?: StatusOrdemCompra;
  /** Busca, período, ordenação e filtros da paridade (servidor). */
  filtros?: FiltrosOrdemCompra;
}

export const useOrdemCompra = ({ pagina = 1, tamanhoPagina = 20, status, filtros = {} }: UseOrdemCompraParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['ordem-compra', pagina, tamanhoPagina, status, filtros],
    queryFn: () => ordemCompraService.getAll(pagina, tamanhoPagina, status, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada filtro
  });

  const ordens       = paginado?.itens       ?? [];
  const total        = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const porStatus    = paginado?.porStatus;
  const valorTotal   = paginado?.valorTotal;

  const createMutation = useMutation({
    mutationFn: (request: CriarOrdemCompraRequest) => ordemCompraService.create(request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ordem-compra'] });
      toast.success(`PO #${data.numero} criada com sucesso`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarOrdemCompraRequest }) =>
      ordemCompraService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordem-compra'] });
      toast.success('Ordem de compra atualizada');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const enviarMutation = useMutation({
    mutationFn: (id: number) => ordemCompraService.enviar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordem-compra'] });
      toast.success('Ordem enviada para aprovação');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const aprovarMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: AprovarOrdemCompraRequest }) =>
      ordemCompraService.aprovar(id, request),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ordem-compra'] });
      toast.success(data.status === 'Aprovada' ? 'Ordem aprovada' : 'Ordem rejeitada');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const enviarFornecedorMutation = useMutation({
    mutationFn: (id: number) => ordemCompraService.enviarParaFornecedor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordem-compra'] });
      toast.success('Ordem enviada ao fornecedor');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const receberMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: ProcessarRecebimentoRequest }) =>
      ordemCompraService.processarRecebimento(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordem-compra'] });
      toast.success('Recebimento processado — Three-Way Match concluído e contas a pagar gerado');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const cancelarMutation = useMutation({
    mutationFn: (id: number) => ordemCompraService.cancelar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ordem-compra'] });
      toast.success('Ordem cancelada');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    ordens,
    total,
    totalPaginas,
    porStatus,
    valorTotal,
    isLoading,
    isFetching,
    criarOrdem:         createMutation.mutateAsync,
    atualizarOrdem:     updateMutation.mutateAsync,
    enviarOrdem:        enviarMutation.mutateAsync,
    aprovarOrdem:       aprovarMutation.mutateAsync,
    enviarFornecedor:   enviarFornecedorMutation.mutateAsync,
    processarRecebimento: receberMutation.mutateAsync,
    cancelarOrdem:      cancelarMutation.mutateAsync,
    isCreating:         createMutation.isPending,
    isUpdating:         updateMutation.isPending,
    isEnviando:         enviarMutation.isPending,
    isAprovando:        aprovarMutation.isPending,
    isRecebendo:        receberMutation.isPending,
  };
};

export const useOrdemCompraDetalhe = (id: number) => {
  return useQuery({
    queryKey: ['ordem-compra', id],
    queryFn: () => ordemCompraService.getById(id),
    enabled: id > 0,
  });
};
